import React, { useState } from "react";
import styled from "styled-components";
import {
    DocumentUploader,
    StandardModal,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSectionTitle,
    Toast
} from "src/components";
import { FormField } from "src/components";
import { MODE_SOURCE } from "src/components/Documents/utils/documentHelpers";
import { adminUploadDocuments } from "src/components/Documents/api/documentsApi";

const Label = styled.label`
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    color: #000;
    margin-bottom: 6px;
    text-align: left;
`;

const StyledInput = styled(FormField)`
    width: 100%;
    height: 39px;
    /* UI Properties */
    background: #FFFFFF 0% 0% no-repeat padding-box;
    box-shadow: 0px 3px 6px #00000029;
    border: 1px solid #D8DEEC;
    border-radius: 7px;
    opacity: 1;
    text-align: left;
`;

const UserGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    width: 100%;
    margin-top: 15px;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;
const Field = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    width: 100%;
`;

const CloseButton = styled.button`
    width: 140px;
    height: 38px;
    background: #fff;
    border: 1px solid #000000;
    border-radius: 7px;
    cursor: pointer;
`;

export default function ClientDocumentsModal({
    open,
    onClose,
    headerTitle,
    requiredDocuments = [],
    refresh,
    userDetails = {}
}) {
    const [hasChanges, setHasChanges] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [saving, setSaving] = useState(false);

    const handleClose = async () => {
        if (hasChanges) {
            await refresh?.();
        }
        onClose?.();
    };

    if (!open) return null;

    const {
        employeeUuid,
        firstName,
        lastName,
        customCompanyName,
        email,
        phoneNumber,
        referralCode
    } = userDetails;

    const hasPendingUploads = documents.some(
        doc => doc && !doc?.uploaded
    );

    const handleSaveDocuments = async () => {
        try {
            setSaving(true);

            await adminUploadDocuments({
                requiredDocuments,
                documents,
                employeeUuid
            });

            Toast.success("Documents uploaded successfully");

            setHasChanges(true);

            await refresh?.();
        } catch (err) {
            console.error(err);
            Toast.error("Failed to upload documents");
        } finally {
            setSaving(false);
        }
    };

    return (
        <StandardModal
            open={open}
            onClose={handleClose}
            widthMode="large"
            title={headerTitle}
            description="View and manage documents uploaded by this user. You can preview, download, and review each file below."
            footer={(
                <StandardModalActionGroup $pushRight>
                    <CloseButton onClick={handleClose}>
                        Close
                    </CloseButton>
                    {hasPendingUploads && (
                        <CloseButton
                            style={{
                                background: "#171A22",
                                color: "#FFF",
                                border: "none"
                            }}
                            onClick={handleSaveDocuments}
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save Documents"}
                        </CloseButton>
                    )}
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <StandardModalSectionTitle>User Details</StandardModalSectionTitle>
                <UserGrid>
                    <Field>
                        <Label>First Name</Label>
                        <StyledInput value={firstName || ""} readOnly />
                    </Field>

                    <Field>
                        <Label>Last Name</Label>
                        <StyledInput value={lastName || ""} readOnly />
                    </Field>

                    <Field>
                        <Label>Company Name</Label>
                        <StyledInput value={customCompanyName || ""} readOnly />
                    </Field>

                    <Field>
                        <Label>Email</Label>
                        <StyledInput value={email || ""} readOnly />
                    </Field>

                    <Field>
                        <Label>Mobile</Label>
                        <StyledInput value={phoneNumber || ""} readOnly />
                    </Field>

                    <Field>
                        <Label>Referral Code</Label>
                        <StyledInput value={referralCode || "-"} readOnly />
                    </Field>
                </UserGrid>
            </StandardModalSection>

            <StandardModalSection>
                <DocumentUploader
                    requiredDocuments={requiredDocuments}
                    title="Documents Uploaded"
                    mode={MODE_SOURCE.SALES_AND_PROMOTION}
                    color="#171A22"
                    refresh={refresh}   
                    setHasChanges={setHasChanges}
                    onChange={({ documents }) => {
                        setDocuments(documents);
                    }}
                />
            </StandardModalSection>
        </StandardModal>
    );
}
